import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  BudgetViewHeader,
  BudgetClientInfo,
  BudgetItemsView,
  BudgetTimeline,
  BudgetComments,
  BudgetShortcuts,
  BudgetExportShare,
  BudgetPDFPreview,
  BudgetItem,
  TimelineEvent,
  Comment
} from "@/components/budgets/view";
import { BudgetStatusBadge, BudgetStatus } from "@/components/budgets/BudgetStatusBadge";
import {
  ArrowLeft,
  Edit,
  Copy,
  Mail,
  Printer,
  FileText,
  Calendar,
  User,
  Building,
  DollarSign,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Settings,
  Bell,
  Eye,
  Download,
  Share2,
  MessageSquare,
  History,
  Package,
  Users,
  FileSpreadsheet,
  PenTool,
  RefreshCw
} from "lucide-react";

// Mock data - substituir com dados reais da API
const mockBudget = {
  id: "1",
  number: "ORC-2024-001",
  status: "sent" as BudgetStatus,
  createdAt: "2024-01-15T10:00:00Z",
  validUntil: "2024-02-15",
  updatedAt: "2024-01-16T14:30:00Z",
  version: 1,
  client: {
    id: "client-1",
    name: "Empresa ABC Ltda",
    document: "12.345.678/0001-90",
    email: "contato@empresaabc.com.br",
    phone: "(11) 1234-5678",
    address: "Rua das Empresas, 123 - São Paulo/SP",
    city: "São Paulo",
    state: "SP",
    zipCode: "01234-567",
    contactName: "João Mendes",
    industry: "Tecnologia",
    notes: "Cliente premium, necessita de acompanhamento próximo"
  },
  responsible: "João Silva",
  paymentTerms: "30 dias",
  deliveryTerms: "A combinar",
  validityDays: 30,
  notes: "Orçamento válido para serviços de consultoria em TI durante o primeiro trimestre de 2024. Desconto especial para novos clientes.",
  items: [
    {
      id: "1",
      code: "CONS001",
      description: "Consultoria em TI - Planejamento Estratégico",
      details: "Análise completa da infraestrutura atual e planejamento de migração para cloud",
      quantity: 40,
      unit: "H",
      unitPrice: 500.00,
      discount: 10,
      discountType: "percentage" as const,
      total: 18000.00,
      category: "Consultoria",
      taxable: true,
      order: 1
    },
    {
      id: "2",
      code: "DEV001",
      description: "Desenvolvimento Web - Sistema Interno",
      details: "Desenvolvimento de sistema de gestão interna com React e Node.js",
      quantity: 80,
      unit: "H",
      unitPrice: 150.00,
      discount: 5,
      discountType: "percentage" as const,
      total: 11400.00,
      category: "Desenvolvimento",
      taxable: true,
      order: 2
    },
    {
      id: "3",
      code: "TRAIN001",
      description: "Treinamento da Equipe",
      details: "Capacitação da equipe nas novas tecnologias implementadas",
      quantity: 20,
      unit: "H",
      unitPrice: 200.00,
      discount: 0,
      discountType: "percentage" as const,
      total: 4000.00,
      category: "Treinamento",
      taxable: true,
      order: 3
    }
  ],
  tags: ["prioridade-alta", "cliente-vip", "tecnologia"]
};

// Mock timeline events
const mockTimelineEvents: TimelineEvent[] = [
  {
    id: "event-1",
    type: "created",
    timestamp: "2024-01-15T10:00:00Z",
    user: {
      id: "user-1",
      name: "João Silva",
      email: "joao@empresa.com",
      avatar: ""
    },
    details: {
      title: "Orçamento criado",
      description: "Orçamento ORC-2024-001 criado para Empresa ABC Ltda",
      metadata: {
        totalItems: 3,
        totalValue: 33400.00
      }
    },
    visibility: "internal",
    importance: "medium"
  },
  {
    id: "event-2",
    type: "edited",
    timestamp: "2024-01-15T14:30:00Z",
    user: {
      id: "user-1",
      name: "João Silva",
      email: "joao@empresa.com",
      avatar: ""
    },
    details: {
      title: "Orçamento atualizado",
      description: "Ajustados valores e condições de pagamento",
      changes: [
        {
          field: "Condições de Pagamento",
          oldValue: "15 dias",
          newValue: "30 dias"
        },
        {
          field: "Desconto item 1",
          oldValue: "5%",
          newValue: "10%"
        }
      ]
    },
    visibility: "internal",
    importance: "low"
  },
  {
    id: "event-3",
    type: "sent",
    timestamp: "2024-01-16T09:15:00Z",
    user: {
      id: "user-1",
      name: "João Silva",
      email: "joao@empresa.com",
      avatar: ""
    },
    details: {
      title: "Enviado para cliente",
      description: "Orçamento enviado por email para contato@empresaabc.com.br",
      metadata: {
        sentTo: "contato@empresaabc.com.br",
        method: "email"
      }
    },
    visibility: "public",
    importance: "high"
  },
  {
    id: "event-4",
    type: "viewed",
    timestamp: "2024-01-16T16:45:00Z",
    user: {
      id: "client-1",
      name: "João Mendes",
      email: "joao.mendes@empresaabc.com.br",
      avatar: ""
    },
    details: {
      title: "Visualizado pelo cliente",
      description: "Cliente visualizou o orçamento",
      metadata: {
        viewDuration: "5 minutos",
        device: "Desktop"
      }
    },
    visibility: "public",
    importance: "medium"
  },
  {
    id: "event-5",
    type: "commented",
    timestamp: "2024-01-17T11:20:00Z",
    user: {
      id: "client-1",
      name: "João Mendes",
      email: "joao.mendes@empresaabc.com.br",
      avatar: ""
    },
    details: {
      title: "Comentário adicionado",
      description: "Cliente solicitou ajuste no prazo de entrega",
      metadata: {
        comment: "Precisaríamos que o treinamento fosse realizado em março. É possível ajustar?"
      }
    },
    visibility: "public",
    importance: "high"
  }
];

// Mock comments
const mockComments: Comment[] = [
  {
    id: "comment-1",
    content: "Cliente tem potencial para upgrade dos serviços. Já demonstrou interesse em consultoria avançada.",
    author: {
      id: "user-1",
      name: "João Silva",
      email: "joao@empresa.com",
      role: "Consultor Sênior"
    },
    type: "internal",
    visibility: "team",
    priority: "medium",
    status: "open",
    tags: ["oportunidade", "upgrade"],
    createdAt: "2024-01-15T11:00:00Z",
    updatedAt: "2024-01-15T11:00:00Z",
    replies: [
      {
        id: "reply-1",
        content: "Concordo. Vamos preparar uma proposta adicional com serviços premium.",
        author: {
          id: "user-2",
          name: "Maria Santos",
          email: "maria@empresa.com",
          role: "Gerente Comercial"
        },
        type: "internal",
        visibility: "team",
        priority: "medium",
        status: "open",
        createdAt: "2024-01-15T14:30:00Z",
        updatedAt: "2024-01-15T14:30:00Z"
      }
    ]
  },
  {
    id: "comment-2",
    content: "Orçamento aprovado! Podemos iniciar os trabalhos na próxima semana.",
    author: {
      id: "client-1",
      name: "João Mendes",
      email: "joao.mendes@empresaabc.com.br",
      role: "Gerente de TI"
    },
    type: "external",
    visibility: "public",
    priority: "high",
    status: "resolved",
    tags: ["aprovado"],
    createdAt: "2024-01-18T10:15:00Z",
    updatedAt: "2024-01-18T10:15:00Z",
    resolvedBy: {
      id: "user-1",
      name: "João Silva"
    },
    resolvedAt: "2024-01-18T10:20:00Z"
  }
];

const currentUser = {
  id: "user-1",
  name: "João Silva",
  email: "joao@empresa.com",
  role: "Consultor Sênior",
  permissions: ["edit_all_comments", "delete_all_comments", "resolve_comments", "view_internal_comments"]
};

export default function BudgetViewPage() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState<'overview' | 'items' | 'client' | 'comments' | 'timeline'>('overview');
  const [budget, setBudget] = useState(mockBudget);
  const [items, setItems] = useState<BudgetItem[]>(mockBudget.items);
  const [comments, setComments] = useState<Comment[]>(mockComments);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>(mockTimelineEvents);

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
    const totalDiscount = items.reduce((sum, item) => {
      const itemSubtotal = item.unitPrice * item.quantity;
      const discountAmount = item.discountType === 'percentage'
        ? itemSubtotal * (item.discount / 100)
        : item.discount;
      return sum + discountAmount;
    }, 0);
    const itemsTotal = items.reduce((sum, item) => sum + item.total, 0);
    const taxableTotal = items.filter(item => item.taxable).reduce((sum, item) => sum + item.total, 0);
    const taxes = taxableTotal * 0.12; // 12% de impostos
    const finalTotal = itemsTotal + taxes;

    return { subtotal, totalDiscount, itemsTotal, taxableTotal, taxes, finalTotal };
  };

  const totals = calculateTotals();

  // Event handlers
  const handleStatusChange = (newStatus: BudgetStatus, justification?: string) => {
    setBudget(prev => ({ ...prev, status: newStatus }));
    // Add timeline event
    const newEvent: TimelineEvent = {
      id: `event-${Date.now()}`,
      type: newStatus === 'approved' ? 'approved' : newStatus === 'rejected' ? 'rejected' : 'edited',
      timestamp: new Date().toISOString(),
      user: currentUser,
      details: {
        title: `Status alterado para ${newStatus}`,
        description: justification || `Orçamento marcado como ${newStatus}`
      },
      visibility: 'internal',
      importance: 'high'
    };
    setTimelineEvents(prev => [newEvent, ...prev]);
  };

  const handleEditBudget = () => {
    // Navigate to edit page or open edit dialog
    window.location.href = `/services/budgets/${id}/edit`;
  };

  const handleDuplicateBudget = () => {
    // Navigate to duplicate page
    window.location.href = `/services/budgets/${id}/duplicate`;
  };

  const handleSendEmail = (type: 'email' | 'whatsapp') => {
    // Handle email/WhatsApp sending
    console.log(`Sending ${type} for budget ${budget.number}`);
  };

  const handleExportPDF = (options: any) => {
    // Handle PDF export
    console.log('Exporting PDF with options:', options);
  };

  const handleExportExcel = (options: any) => {
    // Handle Excel export
    console.log('Exporting Excel with options:', options);
  };

  const handlePrint = (options: any) => {
    // Handle printing
    console.log('Printing with options:', options);
  };

  const handleShareLink = (options: any) => {
    // Handle link sharing
    console.log('Sharing link with options:', options);
  };

  const handleSignDocument = (signatures: Record<string, any>) => {
    // Handle document signing
    console.log('Document signed with signatures:', signatures);
    setBudget(prev => ({ ...prev, status: 'approved' }));
  };

  const handleClientUpdate = (clientData: any) => {
    setBudget(prev => ({
      ...prev,
      client: { ...prev.client, ...clientData }
    }));
  };

  const handleConditionsUpdate = (conditions: any) => {
    setBudget(prev => ({ ...prev, ...conditions }));
  };

  const handleAddComment = (comment: any) => {
    const newComment: Comment = {
      ...comment,
      id: `comment-${Date.now()}`,
      author: currentUser,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      replies: []
    };
    setComments(prev => [newComment, ...prev]);
  };

  const handleEditComment = (commentId: string, content: string) => {
    setComments(prev => prev.map(comment =>
      comment.id === commentId
        ? { ...comment, content, updatedAt: new Date().toISOString() }
        : comment
    ));
  };

  const handleDeleteComment = (commentId: string) => {
    setComments(prev => prev.filter(comment => comment.id !== commentId));
  };

  const handleResolveComment = (commentId: string) => {
    setComments(prev => prev.map(comment =>
      comment.id === commentId
        ? {
            ...comment,
            status: 'resolved' as const,
            resolvedBy: currentUser,
            resolvedAt: new Date().toISOString()
          }
        : comment
    ));
  };

  const handleReplyToComment = (commentId: string, reply: any) => {
    const newReply: Comment = {
      ...reply,
      id: `reply-${Date.now()}`,
      author: currentUser,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setComments(prev => prev.map(comment =>
      comment.id === commentId
        ? { ...comment, replies: [...(comment.replies || []), newReply] }
        : comment
    ));
  };

  const handleAddTimelineEvent = (event: any) => {
    const newEvent: TimelineEvent = {
      ...event,
      id: `event-${Date.now()}`,
      timestamp: new Date().toISOString()
    };
    setTimelineEvents(prev => [newEvent, ...prev]);
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <BudgetViewHeader
        budget={{
          ...budget,
          total: totals.finalTotal
        }}
        onStatusChange={handleStatusChange}
        onEdit={handleEditBudget}
        onDuplicate={handleDuplicateBudget}
        onSend={handleSendEmail}
        onExportPDF={handleExportPDF}
        onPrint={handlePrint}
        onShare={handleShareLink}
        onConvert={() => console.log('Convert to sale')}
        onAddComment={() => setActiveTab('comments')}
        onRemind={() => console.log('Send reminder')}
        showVersionHistory={true}
      />

      {/* Export and Share Controls */}
      <div className="flex justify-end">
        <div className="flex gap-2">
          <BudgetPDFPreview
            budget={budget}
            onGeneratePDF={handleExportPDF}
            onSignDocument={handleSignDocument}
            allowSignature={budget.status !== 'approved'}
          />
          <BudgetExportShare
            budget={budget}
            onExportPDF={handleExportPDF}
            onExportExcel={handleExportExcel}
            onShareLink={handleShareLink}
            onSendEmail={() => handleSendEmail('email')}
            onSendWhatsApp={() => handleSendEmail('whatsapp')}
            onPrint={handlePrint}
          />
          <BudgetShortcuts
            budgetId={budget.id}
            enableKeyboardShortcuts={true}
            enableContextMenu={true}
            showHelpButton={true}
          />
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={(value: typeof activeTab) => setActiveTab(value)}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Visão Geral
          </TabsTrigger>
          <TabsTrigger value="items" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Itens ({items.length})
          </TabsTrigger>
          <TabsTrigger value="client" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Cliente
          </TabsTrigger>
          <TabsTrigger value="comments" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Comentários ({comments.filter(c => c.status === 'open').length})
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Histórico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Budget Items Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Resumo dos Itens
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {items.map((item, index) => (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{item.description}</span>
                            {item.category && (
                              <Badge variant="outline" className="text-xs">{item.category}</Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {item.quantity} {item.unit} × {formatCurrency(item.unitPrice)}
                            {item.discount > 0 && (
                              <span className="text-green-600 ml-2">
                                (-{item.discountType === 'percentage' ? `${item.discount}%` : formatCurrency(item.discount)})
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">{formatCurrency(item.total)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Timeline Events */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Atividades Recentes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {timelineEvents.slice(0, 3).map((event) => (
                      <div key={event.id} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{event.details.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(event.timestamp).toLocaleDateString('pt-BR')} • {event.user.name}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Financial Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Resumo Financeiro
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  {totals.totalDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Descontos:</span>
                      <span>-{formatCurrency(totals.totalDiscount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span>Total de Serviços:</span>
                    <span>{formatCurrency(totals.itemsTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Impostos (12%):</span>
                    <span>{formatCurrency(totals.taxes)}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total Final:</span>
                      <span className="text-primary">
                        {formatCurrency(totals.finalTotal)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Status and Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Status e Ações</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2">
                    <BudgetStatusBadge status={budget.status} />
                    <span className="text-sm text-muted-foreground">
                      {budget.status === 'sent' && 'Aguardando resposta do cliente'}
                      {budget.status === 'approved' && 'Aprovado! Pronto para iniciar'}
                      {budget.status === 'draft' && 'Em elaboração'}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {budget.status === 'sent' && (
                      <Button className="w-full" onClick={() => handleStatusChange('approved')}>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Marcar como Aprovado
                      </Button>
                    )}
                    {budget.status === 'approved' && (
                      <Button className="w-full bg-green-600 hover:bg-green-700">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Converter em Venda
                      </Button>
                    )}
                    <Button variant="outline" className="w-full" onClick={() => handleSendEmail('email')}>
                      <Mail className="h-4 w-4 mr-2" />
                      Enviar Lembrete
                    </Button>
                    <Button variant="outline" className="w-full" onClick={handleEditBudget}>
                      <Edit className="h-4 w-4 mr-2" />
                      Editar Orçamento
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="items">
          <BudgetItemsView
            items={items}
            onItemsChange={setItems}
            editable={true}
            showDetails={true}
            showTaxes={true}
            allowBatchActions={true}
          />
        </TabsContent>

        <TabsContent value="client">
          <BudgetClientInfo
            client={budget.client}
            responsible={budget.responsible}
            paymentTerms={budget.paymentTerms}
            deliveryTerms={budget.deliveryTerms}
            validityDays={budget.validityDays}
            notes={budget.notes}
            onClientUpdate={handleClientUpdate}
            onConditionsUpdate={handleConditionsUpdate}
            editable={true}
            showClientLink={true}
          />
        </TabsContent>

        <TabsContent value="comments">
          <BudgetComments
            comments={comments}
            budgetId={budget.id}
            currentUser={currentUser}
            onAddComment={handleAddComment}
            onEditComment={handleEditComment}
            onDeleteComment={handleDeleteComment}
            onResolveComment={handleResolveComment}
            onReplyToComment={handleReplyToComment}
            allowExternalComments={true}
            allowInternalComments={true}
            allowFileAttachments={true}
          />
        </TabsContent>

        <TabsContent value="timeline">
          <BudgetTimeline
            events={timelineEvents}
            budgetId={budget.id}
            onAddEvent={handleAddTimelineEvent}
            onEditEvent={() => {}}
            onDeleteEvent={() => {}}
            onResolveComment={() => {}}
            showFilters={true}
            allowAddEvents={true}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}